import { useEffect, useState, type FormEvent } from "react";
import { asDecimalString } from "@shop-ledger/shared-types";
import { Button, Input } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { getLoyaltySettings, updateLoyaltySettings } from "../../api/loyaltyApi";
import { StatusBanner } from "../../components/StatusBanner";
import { ApiError } from "../../lib/apiClient";
import { useAsyncData } from "../../lib/useAsyncData";

/**
 * Loyalty program settings — `GET/PATCH /tenant-settings/loyalty` (§15.4). Two rates: how
 * many points a customer earns per currency unit spent, and how much (in currency) each
 * point is worth on redemption. Both default to `0` — a tenant that never sets these has
 * loyalty effectively inactive, which this page surfaces explicitly rather than silently.
 * Per-customer point balances live on `GET /loyalty/customers/{id}` (read-only, no
 * standalone page here — surfaced from the Customers page would be a natural follow-up;
 * not built in this pass since there's no customer detail page to hang it off yet).
 */
export function LoyaltySettingsPage() {
  const { user, ensureFreshToken } = useAuth();
  const canWrite = user?.role === "OWNER";
  const settingsState = useAsyncData(() => ensureFreshToken().then((token) => getLoyaltySettings(token)), []);

  const [earnRate, setEarnRate] = useState("");
  const [redemptionValue, setRedemptionValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (settingsState.data) {
      setEarnRate(settingsState.data.loyalty_points_per_currency_unit);
      setRedemptionValue(settingsState.data.loyalty_point_redemption_value);
    }
  }, [settingsState.data]);

  async function handleSave(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setSuccess(false);
    setSubmitting(true);
    try {
      const token = await ensureFreshToken();
      await updateLoyaltySettings(token, {
        loyalty_points_per_currency_unit: asDecimalString(earnRate),
        loyalty_point_redemption_value: asDecimalString(redemptionValue),
      });
      setSuccess(true);
      settingsState.reload();
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : "Could not save loyalty settings.");
    } finally {
      setSubmitting(false);
    }
  }

  const inactive = settingsState.data && settingsState.data.loyalty_points_per_currency_unit === "0.0000";

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Loyalty Program</h1>
          <p className="page__subtitle">Earn/redeem rate for the points program — points are earned and redeemed automatically at sale time for a sale with a customer attached.</p>
        </div>
      </div>

      {settingsState.error ? <StatusBanner kind="error">{settingsState.error}</StatusBanner> : null}
      {inactive ? <StatusBanner kind="warning">Earn rate is 0 — loyalty is effectively inactive until you set a rate below.</StatusBanner> : null}
      {!canWrite && user ? <StatusBanner kind="info">Your role ({user.role}) can view loyalty settings. Editing is OWNER-only.</StatusBanner> : null}

      <div className="panel">
        <h2 className="panel__title">Earn / redemption rate</h2>
        {settingsState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <form onSubmit={handleSave}>
            <div className="form-grid">
              <Input
                label="Points earned per currency unit spent"
                inputMode="decimal"
                value={earnRate}
                onChange={(e) => setEarnRate(e.target.value)}
                placeholder="e.g. 0.01 (1 point per 100 spent)"
                disabled={!canWrite}
                required
              />
              <Input
                label="Currency value per point redeemed"
                inputMode="decimal"
                value={redemptionValue}
                onChange={(e) => setRedemptionValue(e.target.value)}
                placeholder="e.g. 1.00"
                disabled={!canWrite}
                required
              />
            </div>
            {formError ? <StatusBanner kind="error">{formError}</StatusBanner> : null}
            {success ? <StatusBanner kind="info">Saved.</StatusBanner> : null}
            {canWrite ? (
              <div className="form-actions">
                <Button type="submit" disabled={submitting}>
                  {submitting ? "Saving…" : "Save"}
                </Button>
              </div>
            ) : null}
          </form>
        )}
      </div>
    </div>
  );
}
